import { useEffect, useState, useRef } from "react";
import { useHookWaMessage } from "../../../../../hooks/useHookWaMessage";
import { SendWaMessage } from "../modals/send_message";
import { WaMessageService } from "../../../../../services/WaMessageService";
import Pagination from "../../../../../components/pagination/pagination";
import { FaMagnifyingGlass, FaFilter, FaXmark, FaRotateLeft, FaArrowDownShortWide, FaArrowUpShortWide } from "react-icons/fa6";

// --- COMPONENT: ResendResultModal ---
const ResendResultModal = ({ open, onClose, resultData }) => {
    if (!open || !resultData) return null;
    const { message, data = [] } = resultData;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-fade-in">
            <div className="bg-slate-900 border border-slate-700/80 w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-800/40">
                    <div>
                        <h3 className="text-lg font-bold text-slate-100">Hasil Pengiriman Ulang</h3>
                        <p className="text-xs text-slate-400 mt-0.5">{message}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-800 transition-colors">✕</button>
                </div>
                <div className="p-5 overflow-y-auto space-y-3 flex-1">
                    {data.map((item) => {
                        const isSuccess = item.status === "terkirim";
                        return (
                            <div key={item.log_id} className={`p-3.5 rounded-xl border text-sm flex flex-col gap-1 transition-all ${isSuccess ? "bg-emerald-500/5 border-emerald-500/20" : "bg-red-500/5 border-red-500/20"}`}>
                                <div className="flex justify-between items-center">
                                    <span className="font-mono text-slate-200 font-medium">{item.recipient}</span>
                                    <span className={`px-2 py-0.5 text-xs rounded-full font-semibold capitalize ${isSuccess ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-red-500/20 text-red-400 border border-red-500/30"}`}>{item.status}</span>
                                </div>
                                <p className={`text-xs ${isSuccess ? "text-emerald-300/80" : "text-red-300/80"}`}>{item.note}</p>
                            </div>
                        );
                    })}
                </div>
                <div className="p-4 border-t border-slate-800 bg-slate-800/20 flex justify-end">
                    <button onClick={onClose} className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm transition-colors">Tutup</button>
                </div>
            </div>
        </div>
    );
};

// --- COMPONENT: WaMessageFilterDrawer ---
const FILTER_DEFAULTS = {
    sender: "",
    recipient: "",
    order_by: "created_at",
    sort_dir: "desc",
    per_page: 10,
};

const SORT_OPTIONS = [
    { value: "created_at", label: "Tanggal Dibuat" },
    { value: "id", label: "ID" },
];

const WaMessageFilterDrawer = ({ open, setOpen, currentParams, onApply }) => {
    const panelRef = useRef(null);
    const [form, setForm] = useState(FILTER_DEFAULTS);

    useEffect(() => {
        if (open) {
            setForm({
                sender: currentParams.sender || "",
                recipient: currentParams.recipient || "",
                order_by: currentParams.order_by || "created_at",
                sort_dir: currentParams.sort_dir || "desc",
                per_page: currentParams.per_page || 10,
            });
        }
    }, [open, currentParams]);

    useEffect(() => {
        if (open) document.body.style.overflow = "hidden";
        else document.body.style.overflow = "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    const handleReset = () => {
        setForm(FILTER_DEFAULTS);
    };

    const handleApply = () => {
        onApply(form);
        setOpen(false);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) setOpen(false);
    };

    const activeCount = Object.entries(form).filter(([key, val]) => {
        if (["sort_by", "sort_dir", "order_by"].includes(key)) return false;
        return val !== "" && val !== null && val !== undefined;
    }).length;

    return (
        <>
            <div className={`fixed inset-0 z-[998] bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`} onClick={handleBackdropClick} />
            <div ref={panelRef} className={`fixed top-0 right-0 z-[999] h-full w-full max-w-md shadow-2xl transition-transform duration-300 ease-out ${open ? "translate-x-0" : "translate-x-full"}`} style={{ background: "rgba(15, 23, 42, 0.98)", borderLeft: "1px solid rgba(51, 65, 85, 0.3)", backdropFilter: "blur(20px)" }}>
                <div className="flex items-center justify-between px-6 py-5" style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.25)" }}>
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(0, 180, 216, 0.12)", border: "1px solid rgba(0, 180, 216, 0.2)" }}>
                            <FaFilter className="text-sm text-cyan-400" />
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-100 text-sm">Filter Pesan</h3>
                            {activeCount > 0 && <p className="text-[10px] text-cyan-400 mt-0.5">{activeCount} filter aktif</p>}
                        </div>
                    </div>
                    <button onClick={() => setOpen(false)} className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all"><FaXmark size={18} /></button>
                </div>
                <div className="overflow-y-auto px-6 py-5 space-y-6" style={{ height: "calc(100% - 140px)" }}>
                    <div>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Pencarian</p>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">No. Pengirim (Sender)</label>
                                <input type="text" placeholder="Cth: 08123456789" value={form.sender} onChange={(e) => setForm((f) => ({ ...f, sender: e.target.value }))} className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all" />
                            </div>
                            <div>
                                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">No. Penerima (Recipient)</label>
                                <input type="text" placeholder="Cth: 08198765432" value={form.recipient} onChange={(e) => setForm((f) => ({ ...f, recipient: e.target.value }))} className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all" />
                            </div>
                        </div>
                    </div>
                    <div style={{ borderTop: "1px dashed rgba(51, 65, 85, 0.25)" }} />
                    <div>
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Pengurutan</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">Urutkan</label>
                                <select value={form.order_by} onChange={(e) => setForm((f) => ({ ...f, order_by: e.target.value }))} className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
                                    {SORT_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] text-slate-500 font-semibold mb-1.5 uppercase tracking-wider">Arah</label>
                                <button type="button" onClick={() => setForm((f) => ({ ...f, sort_dir: f.sort_dir === "desc" ? "asc" : "desc" }))} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm rounded-xl border transition-all" style={{ background: form.sort_dir === "desc" ? "rgba(0, 180, 216, 0.08)" : "rgba(251, 146, 60, 0.08)", borderColor: form.sort_dir === "desc" ? "rgba(0, 180, 216, 0.2)" : "rgba(251, 146, 60, 0.2)", color: form.sort_dir === "desc" ? "#22d3ee" : "#fb923c" }}>
                                    {form.sort_dir === "desc" ? (<> <FaArrowDownShortWide size={14} /> Terbaru </>) : (<> <FaArrowUpShortWide size={14} /> Terlama </>)}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div style={{ borderTop: "1px dashed rgba(51, 65, 85, 0.25)" }} />
                    <div className="flex items-center gap-3">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Per halaman</label>
                        <select value={form.per_page} onChange={(e) => setForm((f) => ({ ...f, per_page: e.target.value }))} className="w-full px-3 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all appearance-none cursor-pointer" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2394a3b8' viewBox='0 0 16 16'%3E%3Cpath d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}>
                            <option value="10">10</option>
                            <option value="20">20</option>
                            <option value="50">50</option>
                        </select>
                    </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 flex gap-3 px-6 py-4" style={{ background: "rgba(15, 23, 42, 0.98)", borderTop: "1px solid rgba(51, 65, 85, 0.25)", backdropFilter: "blur(20px)" }}>
                    <button type="button" onClick={handleReset} className="flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all" style={{ background: "rgba(51, 65, 85, 0.15)", borderColor: "rgba(51, 65, 85, 0.3)", color: "#94a3b8" }} onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)"; e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.2)"; e.currentTarget.style.color = "#f87171"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(51, 65, 85, 0.15)"; e.currentTarget.style.borderColor = "rgba(51, 65, 85, 0.3)"; e.currentTarget.style.color = "#94a3b8"; }}>
                        <FaRotateLeft size={13} /> Reset
                    </button>
                    {/* Mengganti BtnDefault dengan button standar agar tidak error */}
                    <button type="button" onClick={handleApply} className="flex-1 justify-center py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all shadow-lg shadow-blue-600/20">
                        Terapkan
                    </button>
                </div>
            </div>
        </>
    );
};

// --- COMPONENT: WaLogActivitySection ---
export const WaLogActivitySection = () => {
    const { data, meta, loading, params, setParams, refreshData } = useHookWaMessage();

    const [selectedIds, setSelectedIds] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [openModalSendMessage, setOpenModalSendMessage] = useState(false);
    const [openFilter, setOpenFilter] = useState(false);
    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [resendResult, setResendResult] = useState(null);

    const columns = [
        { key: "no", label: "No", align: "center", width: "60px" },
        { key: "status", label: "Status", align: "left" },
        { key: "device", label: "Pengirim", align: "left" },
        { key: "recipient", label: "Penerima", align: "left" },
        { key: "message", label: "Pesan", align: "left", width: "250px" },
    ];

    const activeFilterCount = ["sender", "recipient"].filter((key) => params[key] && params[key] !== "").length;

    const handleFilterApply = (filterForm) => {
        setParams((prev) => ({ ...prev, ...filterForm, page: 1 }));
    };

    const handleFilterReset = () => {
        setParams((prev) => ({ ...prev, sender: "", recipient: "", page: 1 }));
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) setSelectedIds(data.map((item) => item.id));
        else setSelectedIds([]);
    };

    const handleSelectRow = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
    };

    const handleResend = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Kirim ulang ${selectedIds.length} pesan yang gagal/terpilih?`)) return;
        setIsProcessing(true);
        try {
            const response = await WaMessageService.resend({ data: { message_ids: selectedIds } });
            if (response.data.status) {
                setResendResult(response.data);
                setResultModalOpen(true);
                refreshData();
                setSelectedIds([]);
            } else {
                alert("Gagal melakukan resend: " + response.data.message);
            }
        } catch (err) {
            console.error("Error Resend:", err);
            alert(err?.response?.data?.message || err?.message || "Terjadi kesalahan tidak terduga.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = async () => {
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Hapus ${selectedIds.length} pesan yang dipilih secara permanen?`)) return;
        setIsProcessing(true);
        try {
            await WaMessageService.delete({ id: selectedIds });
            refreshData();
            setSelectedIds([]);
            alert("Pesan berhasil dihapus.");
        } catch (err) {
            console.error(err);
            alert("Gagal menghapus pesan.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClear = async () => {
        if (!window.confirm("Bersihkan log yang dipilih?")) return;
        setIsProcessing(true);
        try {
            refreshData();
            setSelectedIds([]);
        } catch (err) {
            console.error(err);
        } finally {
            setIsProcessing(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const statusStyles = {
            terkirim: "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20",
            gagal: "bg-red-500/12 text-red-400 border border-red-500/20",
            pending: "bg-yellow-500/12 text-yellow-400 border border-yellow-500/20",
            default: "bg-slate-500/12 text-slate-400 border border-slate-500/20"
        };
        const dotStyles = {
            terkirim: "bg-emerald-400",
            gagal: "bg-red-400",
            pending: "bg-yellow-400",
            default: "bg-slate-400"
        };
        const currentStyle = statusStyles[status] || statusStyles.default;
        const currentDot = dotStyles[status] || dotStyles.default;
        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold tracking-wide capitalize ${currentStyle}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${currentDot}`} />
                {status || "-"}
            </span>
        );
    };

    const FilterChip = ({ label, onRemove }) => (
        <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg text-[11px] font-semibold cursor-default" style={{ background: "rgba(0, 180, 216, 0.08)", border: "1px solid rgba(0, 180, 216, 0.2)", color: "#22d3ee" }}>
            {label}
            <button onClick={onRemove} className="p-0.5 rounded-md hover:bg-cyan-400/20 transition-colors"><FaXmark size={10} /></button>
        </span>
    );

    return (
        <>
            <div className="space-y-4">
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-3 font-medium text-sm">
                        <button type="button" className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors" onClick={() => setOpenModalSendMessage(true)}> + Kirim Pesan </button>
                        <div className="h-6 w-px bg-slate-700 hidden sm:block"></div>
                        <button disabled={selectedIds.length === 0 || isProcessing} className="px-4 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2" onClick={handleResend}>
                            {isProcessing ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null} Kirim Ulang
                        </button>
                        <button disabled={selectedIds.length === 0 || isProcessing} className="px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2" onClick={handleDelete}>
                            {isProcessing ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : null} Hapus Pesan
                        </button>
                        <button disabled={selectedIds.length === 0 || isProcessing} className="px-4 py-2 rounded-xl bg-pink-600 hover:bg-pink-700 text-white disabled:opacity-50 transition-colors" onClick={handleClear}> Bersihkan </button>
                        {selectedIds.length > 0 && ( <span className="ml-auto text-sm text-slate-400 bg-slate-800/50 px-3 py-1 rounded-lg border border-slate-700">{selectedIds.length} dipilih</span> )}
                    </div>
                    <div className="flex items-center gap-3 sticky top-16 z-10 py-2">
                        <div className="relative flex-1">
                            <FaMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-sm" />
                            <input type="text" placeholder="Cari No. Penerima..." value={params.recipient || ""} onChange={(e) => setParams((p) => ({ ...p, recipient: e.target.value, page: 1 }))} className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl bg-slate-800/60 border border-slate-600/40 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all" />
                        </div>
                        <button onClick={() => setOpenFilter(true)} className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all" style={{ background: activeFilterCount > 0 ? "rgba(0, 180, 216, 0.12)" : "rgba(51, 65, 85, 0.2)", border: activeFilterCount > 0 ? "1px solid rgba(0, 180, 216, 0.3)" : "1px solid rgba(51, 65, 85, 0.3)", color: activeFilterCount > 0 ? "#22d3ee" : "#94a3b8" }}>
                            <FaFilter className="text-xs" />
                            <span className="hidden sm:inline">Filter</span>
                            {activeFilterCount > 0 && ( <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black" style={{ background: "#22d3ee", color: "#0f172a" }}>{activeFilterCount}</span> )}
                        </button>
                        {activeFilterCount > 0 && ( <button onClick={handleFilterReset} className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold text-red-400 transition-all" style={{ background: "rgba(239, 68, 68, 0.06)", border: "1px solid rgba(239, 68, 68, 0.15)" }} title="Hapus semua filter"><FaRotateLeft size={11} /><span className="hidden sm:inline">Reset</span></button> )}
                    </div>
                    {activeFilterCount > 0 && (
                        <div className="flex flex-wrap items-center gap-2 animate-fade-in-down">
                            {params.sender && <FilterChip label={`Sender: ${params.sender}`} onRemove={() => setParams((p) => ({ ...p, sender: "", page: 1 }))} />}
                            {params.recipient && <FilterChip label={`Penerima: ${params.recipient}`} onRemove={() => setParams((p) => ({ ...p, recipient: "", page: 1 }))} />}
                        </div>
                    )}
                </div>

                {/* --- Mobile View --- */}
                <div className="block lg:hidden space-y-3 pb-20">
                    <div className="flex items-center gap-2 px-2 py-2 text-slate-400 text-sm">
                        <input type="checkbox" checked={data.length > 0 && selectedIds.length === data.length} onChange={handleSelectAll} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500" />
                        <span>Pilih Semua</span>
                    </div>
                    {loading && <div className="text-center py-10 text-slate-400">Memuat data...</div>}
                    {!loading && data.length === 0 && <div className="text-center py-10 text-slate-400">Tidak ada data log pesan</div>}
                    {!loading && data.map((item, index) => (
                        <div key={item.id} className={`p-4 rounded-xl border transition-all duration-200 ${selectedIds.includes(item.id) ? "border-blue-500/50 bg-blue-500/5" : "border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/60"}`} style={{ backdropFilter: "blur(12px)" }}>
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-3">
                                    <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => handleSelectRow(item.id)} className="w-5 h-5 rounded border-slate-600 bg-slate-900 text-blue-600 focus:ring-blue-500 cursor-pointer" />
                                    <span className="text-xs text-slate-500 font-mono">#{index + 1}</span>
                                </div>
                                <StatusBadge status={item?.status} />
                            </div>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between"><span className="text-slate-400 text-xs uppercase tracking-wide">Pengirim</span><span className="text-slate-200 font-medium text-right ml-4">{item?.device?.phone_number ?? "-"}</span></div>
                                <div className="flex justify-between"><span className="text-slate-400 text-xs uppercase tracking-wide">Penerima</span><span className="text-slate-200 font-medium text-right ml-4 break-all max-w-[60%]">{item?.recipient ?? "-"}</span></div>
                                <div className="pt-2 border-t border-slate-700/50"><p className="text-slate-400 text-xs uppercase tracking-wide mb-1">Pesan</p><p className="text-slate-300 line-clamp-3 leading-relaxed">{item.message}</p></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* --- Desktop View --- */}
                <section className="hidden lg:block rounded-2xl overflow-hidden" style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)", backdropFilter: "blur(12px)" }}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b" style={{ background: "rgba(15, 23, 42, 0.7)", borderColor: "rgba(51, 65, 85, 0.3)" }}>
                                    <th className="px-4 py-3.5 text-center w-12"><input type="checkbox" checked={data.length > 0 && selectedIds.length === data.length} onChange={handleSelectAll} className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-blue-600 focus:ring-blue-500" /></th>
                                    {columns.map((col) => (<th key={col.key} className={`px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-400 ${col.align === "center" ? "text-center" : "text-left"}`}>{col.label}</th>))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (<tr><td colSpan={columns.length + 1} className="text-center py-10 text-slate-400">Memuat data...</td></tr>)}
                                {!loading && data.length === 0 && (<tr><td colSpan={columns.length + 1} className="text-center py-10 text-slate-400">Tidak ada data log pesan</td></tr>)}
                                {!loading && data?.map((item, index) => (
                                    <tr key={item.id} className={`border-b border-slate-800 transition-colors ${selectedIds.includes(item.id) ? "bg-blue-500/5" : "hover:bg-slate-800/30"}`}>
                                        <td className="px-4 py-3 text-center"><input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => handleSelectRow(item.id)} className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-600 focus:ring-blue-500" /></td>
                                        <td className="px-4 py-3 text-center text-slate-500 font-mono text-xs">{meta?.from + index}</td>
                                        <td className="px-4 py-3"><StatusBadge status={item?.status} /></td>
                                        <td className="px-4 py-3 text-slate-300">{item?.device?.phone_number ?? "-"}</td>
                                        <td className="px-4 py-3 text-slate-300">{item?.recipient ?? "-"}</td>
                                        <td className="px-4 py-3 max-w-xs truncate text-slate-400">{item.message}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination meta={meta} params={params} setParams={setParams} />
                </section>
            </div>
            <SendWaMessage openModal={openModalSendMessage} setOpenModal={setOpenModalSendMessage} refresh={refreshData} />
            <ResendResultModal open={resultModalOpen} onClose={() => setResultModalOpen(false)} resultData={resendResult} />
            <WaMessageFilterDrawer open={openFilter} setOpen={setOpenFilter} currentParams={params} onApply={handleFilterApply} />
        </>
    );
};